import React, { useEffect, useRef } from 'react';
import '../Pages/styles.css'; // Import the prefixed CSS
// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Accreditation & Equipment Planning) ---
import {
    // Accreditation & General
    faLock,
    faPills,
    faFolderOpen,
    faComments,
    faAward,            // Accreditation / Certification / JCI / ISO
    faCheckCircle,      // Compliance / Quality / Success
    faShieldAlt,        // Safety / Security / HIPAA / Data Privacy
    faUsers,            // Patient Trust / Staff / Community
    faTasks,            // Operational Efficiency / Workflow / Management
    faBalanceScale,     // Legal / Ethical / Regulatory / Drug Policy
    faGlobe,            // Global Recognition / International Standards
    faSearchPlus,       // Audits / Assessments / Review
    faChalkboardTeacher,// Training / Education
    faSyncAlt,          // Ongoing Support / Continuous Improvement
    faNotesMedical,     // Medical Records / Patient Info
    faBiohazard,        // Infection Control
    faHeartPulse,       // Patient Safety
    faUserMd,           // Operating Theatre (OT) standards (can also use faProcedures)
    faBuilding,         // Facility Certifications
    // Equipment Planning
    faMicroscope,       // Medical Equipment / Technology
    faTools,            // Planning / Maintenance / Operations
    faCogs,             // Integration / Systems / Efficiency
    faDollarSign,       // Cost Efficiency / Budget / ROI
    faLightbulb,        // Planning / Strategy / Future-Proofing
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
    faClipboardList, faHardHat, faUserTie, faClock, faEye, faShoppingCart, faUserHardHat,
    faExclamationTriangle, faDraftingCompass, faFileSignature, faHandshake, faKey,
    faRoute, faChartLine, faWarehouse, faTrophy, faLaptopCode, faClinicMedical, faStethoscope,
    faMapMarkedAlt, faMagnifyingGlassChart, faHandHoldingDollar, faBinoculars, faStore
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons


// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // Importance of Accreditation
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Key Certifications
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // Our Approach (Accreditation)
    const [title4Ref, title4Visible] = useIntersectionObserverTitle(titleObserverOptions); // Equipment Planning Intro
    const [title5Ref, title5Visible] = useIntersectionObserverTitle(titleObserverOptions); // Equipment Planning Benefits

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for standards/quality/equipment
    const heroBgImage = 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Healthcare tech/professional image

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Accreditation, Certifications & Medical Equipment Planning</h1>
            <p className="cliniqor-sub-heading">
              Elevating Healthcare Standards and Facility Operations with Cliniqor
            </p>
            <p className="cliniqor-intro-text">
              Cliniqor guides healthcare facilities through complex accreditation processes and provides expert medical equipment planning, ensuring the highest standards of care, safety, operational excellence, and resource optimization.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- IMPORTANCE OF ACCREDITATION --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              <h2>The Importance of Accreditation in Healthcare</h2>
              <p>More than certification—a commitment to quality, safety, and trust.</p>
            </div>
             {/* Using Features Grid for the 4 main importance points */}
            <div className="cliniqor-features-grid">
                <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUsers} /></div>
                        <h4>1. Patient Trust and Confidence</h4>
                        <p>Inspires confidence through proven quality, safety, and excellence, boosting attractiveness, especially for medical tourism.</p>
                    </div>
                </div>
                <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faTasks} /></div>
                        <h4>2. Operational Efficiency & Risk Management</h4>
                        <p>Streamlines processes, improves resource management, optimizes workflows, and identifies/mitigates operational risks.</p>
                    </div>
                </div>
                <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faBalanceScale} /></div>
                        <h4>3. Compliance with Legal & Ethical Standards</h4>
                        <p>Ensures adherence to patient rights, privacy laws, and safety protocols, avoiding fines and liabilities.</p>
                    </div>
                </div>
                <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faGlobe} /></div>
                        <h4>4. Global Recognition & Competitive Advantage</h4>
                        <p>Sets facilities apart, attracts more patients, and enhances credibility, especially for international recognition (e.g., JCI).</p>
                    </div>
                </div>
            </div>
          </div>
        </section>

        {/* --- KEY CERTIFICATIONS (Detailed Planning Section - 10 items) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Key Certifications and Accreditations We Support</h2>
               <p>Guiding institutions through crucial quality and safety standard processes.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Using Planning Items for the 10 Certifications */}
            {/* This list is long, ensure styling supports it or consider alternative layouts if needed */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faAward} /></div> {/* JCI */}
                    <div className="cliniqor-content"><h4>1. Joint Commission International (JCI)</h4> <p>Global gold standard for patient safety, quality care, and continuous improvement.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faAward} /></div> {/* ISO */}
                    <div className="cliniqor-content"><h4>2. ISO Certifications (9001, 14001, 27001)</h4> <p>Benchmarks for quality management, environmental sustainability, and data security.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faBuilding} /></div>
                    <div className="cliniqor-content"><h4>3. Healthcare Facility Certifications</h4> <p>Ensuring building safety, cleanliness, accessibility, and functionality standards.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faShieldAlt} /></div> {/* HIPAA */}
                    <div className="cliniqor-content"><h4>4. HIPAA Compliance</h4> <p>Safeguarding patient information, ensuring confidentiality and privacy in digital health.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faBiohazard} /></div>
                    <div className="cliniqor-content"><h4>5. Infection Control Certifications</h4> <p>Implementing robust measures for cleanliness, sterilization, and hygiene (HAIs prevention).</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faHeartPulse} /></div>
                    <div className="cliniqor-content"><h4>6. Patient Safety Standards</h4> <p>Adhering to protocols for safe medication, surgery, monitoring, and reducing medical errors.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faUserMd} /></div> {/* OT */}
                    <div className="cliniqor-content"><h4>7. OT Standards and Certifications</h4> <p>Ensuring operating theaters meet high standards for sterility, safety, and infection prevention.</p> </div>
                 </div>
                  <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faLock} /></div> {/* Data Privacy */}
                    <div className="cliniqor-content"><h4>8. Data Privacy and Security</h4> <p>Complying with regulations (e.g., GDPR) for secure storage and handling of patient EHR data.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faPills} /></div> {/* Drug Policy */}
                    <div className="cliniqor-content"><h4>9. Drug Policy and Regulations</h4> <p>Developing clear policies for safe medication administration, storage, and disposal.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faFolderOpen} /></div> {/* Records */}
                    <div className="cliniqor-content"><h4>10. Medical Records Management</h4> <p>Ensuring robust systems for accurate, accessible, and protected patient data.</p> </div>
                 </div>
            </div>
          </div>
        </section>

        {/* --- OUR APPROACH (Accreditation - Features Layout) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
           <div className="cliniqor-container">
              <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Our Approach to Achieving Accreditation and Certification</h2>
                 <p>A tailored strategy ensuring a seamless journey to compliance.</p>
              </div>
              <div className="cliniqor-features-grid">
                    {/* --- Cards for Approach Steps --- */}
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faComments} /></div> {/* Consultation */}
                            <h4>1. Comprehensive Consultation</h4>
                            <p>Understanding needs and goals to develop a tailored accreditation strategy.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faSearchPlus} /></div>
                            <h4>2. Audits and Assessments</h4>
                            <p>Identifying compliance gaps in operations, infrastructure, and processes.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faChalkboardTeacher} /></div>
                            <h4>3. Staff Training and Education</h4>
                            <p>Equipping staff with knowledge on standards, safety, infection control, and best practices.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faSyncAlt} /></div>
                            <h4>4. Ongoing Support and Monitoring</h4>
                            <p>Ensuring continuous compliance with evolving standards for sustained excellence.</p>
                        </div>
                    </div>
               </div>
           </div>
        </section>

        {/* --- MEDICAL EQUIPMENT PLANNING INTRO --- */}
        <section className="cliniqor-section cliniqor-section-bg-gradient"> {/* Use gradient bg */}
           <div className="cliniqor-container">
              <div ref={title4Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title4Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Medical Equipment Planning: Ensuring Excellence in Operations</h2>
                 <p>The backbone of healthcare delivery, vital for quality, safety, and efficiency.</p>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up">
                   <p>Proper planning and integration of medical equipment are crucial for successful hospital operations. Quality, reliability, and appropriateness directly link to healthcare effectiveness. From beds to MRI machines, each piece plays a critical role.</p>
                   <p>At Cliniqor, we specialize in world-class medical equipment planning, ensuring your facility operates with the best resources, optimizing budget and operational needs from identification and sourcing to installation, integration, and maintenance.</p>
              </div>
           </div>
        </section>

        {/* --- EQUIPMENT PLANNING BENEFITS (Features Layout) --- */}
         <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
           <div className="cliniqor-container">
              <div ref={title5Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title5Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Key Benefits of Effective Medical Equipment Planning</h2>
                 <p>Optimizing resources, enhancing safety, ensuring efficiency, and preparing for the future.</p>
              </div>
              <div className="cliniqor-features-grid">
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCogs} /></div> {/* Resource Optimization */}
                            <h4>Optimal Resource Utilization</h4>
                            <p>Acquiring only necessary equipment, avoiding overspending and underutilization.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faHeartPulse} /></div>
                            <h4>Enhanced Patient Safety</h4>
                            <p>Preventing medical errors by ensuring the right tools for accurate diagnosis and treatment.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faDollarSign} /></div>
                            <h4>Cost Efficiency</h4>
                            <p>Minimizing operational costs by selecting reliable, durable equipment meeting long-term needs.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLightbulb} /></div>
                            <h4>Future-Proofing</h4>
                            <p>Ensuring adaptability to accommodate emerging technologies as healthcare evolves.</p>
                        </div>
                    </div>
               </div>
           </div>
        </section>

      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h3>Elevate Your Healthcare Facility with Cliniqor</h3>
          <p>Achieve and maintain essential accreditations and optimize your medical equipment strategy with our expert guidance. Contact us today to ensure your facility meets the highest standards of care, safety, and operational excellence.</p>
          <a href="#contact" className="cliniqor-cta-button">Request Consultation</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;